"use client";
import React from "react";
import cx from "clsx";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { DailyWeatherForecastData } from "@/types/forecast";
import { isNightForDayAtHour } from "@/utils/dayNight";

export type GlyphKind =
  | "clear-day"
  | "clear-night"
  | "partly-day"
  | "partly-night"
  | "cloud"
  | "fog"
  | "rain"
  | "snow"
  | "thunder";

export const PRECIP_GLYPHS: readonly GlyphKind[] = ["rain", "snow", "thunder"];

export const codeToGlyph = (code: number, night: boolean): GlyphKind => {
  if (code === 0) return night ? "clear-night" : "clear-day";
  if (code === 1 || code === 2) return night ? "partly-night" : "partly-day";
  if (code === 3) return "cloud";
  if (code === 45 || code === 48) return "fog";
  if (code >= 71 && code <= 77) return "snow";
  if (code === 85 || code === 86) return "snow";
  if (code >= 95) return "thunder";
  if (code >= 51) return "rain";
  return "cloud";
};

const CLOUD_PATH =
  "M7 17h9a4 4 0 0 0 .6-7.96A5.5 5.5 0 0 0 6 10.06 3.5 3.5 0 0 0 7 17Z";

const Glyph = ({ kind }: { kind: GlyphKind }) => {
  const stroke = {
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.6,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
  };
  switch (kind) {
    case "clear-day":
      return (
        <svg viewBox="0 0 24 24" width={18} height={18} aria-hidden>
          <circle cx="12" cy="12" r="4.2" {...stroke} />
          <path
            d="M12 3.5v2M12 18.5v2M3.5 12h2M18.5 12h2M6 6l1.4 1.4M16.6 16.6 18 18M18 6l-1.4 1.4M7.4 16.6 6 18"
            {...stroke}
          />
        </svg>
      );
    case "clear-night":
      return (
        <svg viewBox="0 0 24 24" width={18} height={18} aria-hidden>
          <path
            d="M19 14.5A7.5 7.5 0 0 1 9.5 5 7 7 0 1 0 19 14.5Z"
            {...stroke}
          />
        </svg>
      );
    case "partly-day":
      return (
        <svg viewBox="0 0 24 24" width={18} height={18} aria-hidden>
          <circle cx="8" cy="8" r="3" {...stroke} />
          <path d="M8 2.5v1.6M2.5 8h1.6M4 4l1.2 1.2" {...stroke} />
          <path
            d="M11 19h6.5a3.5 3.5 0 0 0 .5-6.96A4.8 4.8 0 0 0 8.7 13 3 3 0 0 0 11 19Z"
            {...stroke}
          />
        </svg>
      );
    case "partly-night":
      return (
        <svg viewBox="0 0 24 24" width={18} height={18} aria-hidden>
          <path
            d="M11 8.5A4.5 4.5 0 0 1 7 3a4.2 4.2 0 1 0 4 5.5Z"
            {...stroke}
          />
          <path
            d="M11 19h6.5a3.5 3.5 0 0 0 .5-6.96A4.8 4.8 0 0 0 8.7 13 3 3 0 0 0 11 19Z"
            {...stroke}
          />
        </svg>
      );
    case "cloud":
      return (
        <svg viewBox="0 0 24 24" width={18} height={18} aria-hidden>
          <path d={CLOUD_PATH} {...stroke} />
        </svg>
      );
    case "fog":
      return (
        <svg viewBox="0 0 24 24" width={18} height={18} aria-hidden>
          <path
            d="M7 13h9a4 4 0 0 0 .6-7.96A5.5 5.5 0 0 0 6 6.06 3.5 3.5 0 0 0 7 13Z"
            {...stroke}
          />
          <path d="M5 16.5h13M7 19.5h9" {...stroke} />
        </svg>
      );
    case "rain":
      return (
        <svg viewBox="0 0 24 24" width={18} height={18} aria-hidden>
          <path
            d="M7 14h9a4 4 0 0 0 .6-7.96A5.5 5.5 0 0 0 6 7.06 3.5 3.5 0 0 0 7 14Z"
            {...stroke}
          />
          <path
            d="M8.5 16.5 7.5 19M12.5 16.5l-1 2.5M16.5 16.5l-1 2.5"
            {...stroke}
          />
        </svg>
      );
    case "snow":
      return (
        <svg viewBox="0 0 24 24" width={18} height={18} aria-hidden>
          <path
            d="M7 14h9a4 4 0 0 0 .6-7.96A5.5 5.5 0 0 0 6 7.06 3.5 3.5 0 0 0 7 14Z"
            {...stroke}
          />
          <path
            d="M8.2 17.2h.01M12 18.6h.01M15.8 17.2h.01M10 20.4h.01M14 20.4h.01"
            {...stroke}
            strokeWidth={2.2}
          />
        </svg>
      );
    case "thunder":
      return (
        <svg viewBox="0 0 24 24" width={18} height={18} aria-hidden>
          <path
            d="M7 13h9a4 4 0 0 0 .6-7.96A5.5 5.5 0 0 0 6 6.06 3.5 3.5 0 0 0 7 13Z"
            {...stroke}
          />
          <path d="m12.5 14-2 3.5h3L11.5 21" {...stroke} />
        </svg>
      );
  }
};

export type TimelineSegment = {
  from: number;
  to: number;
  glyph: GlyphKind;
  code: number;
};

const MAX_SEGMENTS = 6;

export const buildDaySegments = (
  day?: DailyWeatherForecastData,
): TimelineSegment[] => {
  const codes = day?.hourlyWeatherCode;
  if (!day || !Array.isArray(codes) || !codes.length) return [];

  const raw: TimelineSegment[] = [];
  codes.slice(0, 24).forEach((value, hour) => {
    const code = Number(value);
    if (!Number.isFinite(code)) return;
    const night = isNightForDayAtHour(day, hour);
    const glyph = codeToGlyph(code, night);
    const last = raw[raw.length - 1];
    if (last && last.glyph === glyph && last.to === hour - 1) last.to = hour;
    else raw.push({ from: hour, to: hour, glyph, code });
  });

  const smoothed: TimelineSegment[] = [];
  raw.forEach((segment) => {
    const prev = smoothed[smoothed.length - 1];
    const length = segment.to - segment.from + 1;
    if (prev && (length < 2 || prev.glyph === segment.glyph)) {
      prev.to = segment.to;
    } else {
      smoothed.push({ ...segment });
    }
  });
  if (smoothed.length > 1 && smoothed[0].to - smoothed[0].from + 1 < 2) {
    smoothed[1].from = smoothed[0].from;
    smoothed.shift();
  }

  while (smoothed.length > MAX_SEGMENTS) {
    let shortest = 0;
    smoothed.forEach((s, i) => {
      if (s.to - s.from < smoothed[shortest].to - smoothed[shortest].from)
        shortest = i;
    });
    if (shortest === 0) {
      smoothed[1].from = smoothed[0].from;
      smoothed.shift();
    } else {
      smoothed[shortest - 1].to = smoothed[shortest].to;
      smoothed.splice(shortest, 1);
    }
  }

  return smoothed;
};

export const getDayDisplayCode = (
  day?: DailyWeatherForecastData,
): number | null => {
  const segments = buildDaySegments(day);
  if (!segments.length) return null;
  return segments.reduce(
    (worst, s) => (s.code > worst ? s.code : worst),
    segments[0].code,
  );
};

export const findRepresentativeSegment = (
  segments: TimelineSegment[],
  dayCode?: number,
): number => {
  if (!segments.length) return -1;
  const len = (i: number) => segments[i].to - segments[i].from;
  const pickLongest = (indices: number[]) =>
    indices.reduce(
      (best, i) => (best === -1 || len(i) > len(best) ? i : best),
      -1,
    );

  if (Number.isFinite(dayCode)) {
    const code = Number(dayCode);
    const byCode = pickLongest(
      segments.flatMap((s, i) => (s.code === code ? [i] : [])),
    );
    if (byCode !== -1) return byCode;

    const glyphs = [codeToGlyph(code, false), codeToGlyph(code, true)];
    const byGlyph = pickLongest(
      segments.flatMap((s, i) => (glyphs.includes(s.glyph) ? [i] : [])),
    );
    if (byGlyph !== -1) return byGlyph;
  }

  return pickLongest(segments.map((_, i) => i));
};

export const getSegmentCodeAtHour = (
  day: DailyWeatherForecastData | undefined,
  hour: number,
): number | null => {
  const segment = buildDaySegments(day).find(
    (s) => hour >= s.from && hour <= s.to,
  );
  if (segment) return segment.code;
  const raw = Number(day?.hourlyWeatherCode?.[hour]);
  return Number.isFinite(raw) ? raw : null;
};

const pad = (n: number) => String(n).padStart(2, "0");

type Props = {
  segments: TimelineSegment[];
  activeIndex: number | null;
  onSelect: (index: number) => void;
  isMobile?: boolean;
};

const WeatherTimeline = ({
  segments,
  activeIndex,
  onSelect,
  isMobile,
}: Props) => {
  const { t } = useTranslation();

  if (!segments.length) return null;

  const active = activeIndex !== null ? segments[activeIndex] : null;
  const activeDescription = active
    ? t(`weather.${active.code}.description`)
    : null;

  return (
    <div className="relative z-50 w-full">
      <div className="mb-2 flex items-baseline justify-between gap-2">
        <span
          className={cx(
            "truncate",
            isMobile
              ? "text-xs text-white/60"
              : "text-[10.5px] font-bold tracking-[0.12em] text-white/45 uppercase",
          )}
        >
          {t("insights.timeline")}
        </span>
        {active && (
          <p
            className={cx(
              "shrink-0 font-semibold",
              isMobile ? "text-xs" : "text-sm",
            )}
            aria-live="polite"
          >
            <span className="text-white/80">
              {pad(active.from)}:00–{pad(active.to + 1)}:00
            </span>
            <span className="text-cyan-300">
              &nbsp;·&nbsp;{activeDescription}
            </span>
          </p>
        )}
      </div>

      <ul
        className={cx(
          "flex w-full",
          isMobile ? "surface-card overflow-hidden rounded-xl" : "gap-1",
        )}
        aria-label={t("insights.timeline")}
        role="tablist"
      >
        {segments.map((segment, index) => {
          const length = segment.to - segment.from + 1;
          const isActive = index === activeIndex;
          const description = t(`weather.${segment.code}.description`);
          return (
            <li
              key={`${segment.from}-${segment.glyph}`}
              style={{ flexGrow: length, flexBasis: 0 }}
              className={cx(
                "relative min-w-0",
                isMobile && "border-l border-white/15 first:border-l-0",
              )}
            >
              {isActive && (
                <motion.span
                  layoutId="weather-timeline-active"
                  transition={{ type: "spring", stiffness: 400, damping: 34 }}
                  className={cx(
                    "absolute inset-0",
                    isMobile
                      ? "bg-[#b98bff]/25"
                      : "rounded-lg border border-[rgba(179,151,253,.5)] bg-[linear-gradient(180deg,rgba(151,120,244,.3),rgba(108,84,178,.14))] shadow-[0_0_16px_rgba(139,92,246,.3),inset_0_1px_0_rgba(255,255,255,.12)]",
                  )}
                  aria-hidden
                />
              )}
              <button
                type="button"
                role="tab"
                aria-selected={isActive}
                title={`${description}, ${pad(segment.from)}:00–${pad(segment.to + 1)}:00`}
                onClick={() => onSelect(index)}
                className={cx(
                  "relative flex w-full cursor-pointer items-center justify-center gap-2",
                  isMobile
                    ? "h-11"
                    : "h-10 rounded-lg border border-[rgba(179,151,253,.1)] bg-[rgba(10,8,29,.4)] px-2 duration-200 hover:border-[rgba(179,151,253,.32)]",
                  isActive ? "text-white" : "text-white/60 hover:text-white",
                  isActive && !isMobile && "border-transparent bg-transparent",
                )}
              >
                <Glyph kind={segment.glyph} />
                {!isMobile && length >= 4 && (
                  <span className="truncate text-[11.5px] font-semibold">
                    {description}
                  </span>
                )}
                <span className="sr-only">
                  {description}, {pad(segment.from)}:00–{pad(segment.to + 1)}
                  :00
                </span>
              </button>
            </li>
          );
        })}
      </ul>

      <div className="relative mt-1 flex w-full" aria-hidden>
        {segments.map((segment, index) => (
          <span
            key={`b-${segment.from}`}
            style={{ flexGrow: segment.to - segment.from + 1, flexBasis: 0 }}
            className="min-w-0"
          >
            <span
              className={cx(
                "inline-block text-[11px] text-white/40",
                index !== 0 && "-translate-x-1/2",
              )}
            >
              {pad(segment.from)}
            </span>
          </span>
        ))}
        <span className="absolute right-0 text-[11px] text-white/40">24</span>
      </div>
    </div>
  );
};

export default WeatherTimeline;
